import { useEffect, useState } from 'react'
import { Link, useParams, useHistory } from 'react-router-dom'

import DashboardEbpcApplicationReviewRatingField
    from '../DashboardEbpcApplicationReviewRatingField/DashboardEbpcApplicationReviewRatingField'

import Form from '../../../../components/Form/Form'
import RichText from '../../../../components/RichText/RichText'
import TextArea from '../../../../components/TextArea/TextArea'

import { getCssVar } from '../../../../utils/css'
import { graphqlClient, gql } from '../../../../utils/gql'
import { camelCase } from '../../../../utils/strings'

import authProvider from '../../../../utils/auth-provider'

export default function DashboardEbpcApplicationReviewRate (props = {
    assignment: null,
    assignments: null,
    steps: null,
    onUpdate: null
}) {
    if (!props.assignment)
        return null

    const history = useHistory()

    const params = useParams()

    const formId = `ebpc-assignment-${props.assignment.ebpc_application.id}`

    const [assignmentData, setAssignmentData] = useState(Object
        .assign(history.state || {}, {
            ebpc_ratings: props.assignment.ratingsTypes
                .reduce((acc, curr) => {
                    acc[curr.slug] = (props.assignment.ebpc_ratings
                        .filter(obj => obj.type === curr.slug)[0] || {})
                        .rating || null

                    return acc
                }, {}),
            rating: props.assignment.rating || null,
            invest: typeof props.assignment.invest === 'boolean'
                ? props.assignment.invest
                : null,
            feedback: props.assignment.feedback || null,
            feedback_share: props.assignment.feedback_share || null,
            feedback_admin: props.assignment.feedback_admin || null,
            submitted_at: props.assignment.submitted_at || null,
            ebpc_finals: props.assignment.ebpc_finals || false
        }))

    const [ratingsFields, setRatingsFields] = useState([])

    useEffect(() => {
        setRatingsFields(props.assignment.ratingsTypes
            .map((obj) => {
                const step = props.steps
                    .filter(_obj => camelCase(_obj.slug) === obj.slug)[0]

                if (!step)
                    return null

                obj.resource = 'ebpc_ratings'

                obj.richText = (step.richText || '')
                    .replace(/(.)\*(<\/)/, '$1$2')

                obj.answer = props.assignment.ebpc_application
                    .data[obj.slug][`${obj.slug}Summary`]

                const rating = (props.assignment.ebpc_ratings
                    .filter(_obj => _obj.type === obj.slug)[0] || {})

                obj.rating = assignmentData.ebpc_ratings[obj.slug]
                    || rating.rating
                    || null

                obj.id = rating.id || null

                return obj
            })
            .filter(obj => obj)
            .concat([{
                backgroundColor: getCssVar('--porcelain'),
                resource: 'ebpc_assignments',
                title: 'Overall rating',
                slug: 'rating',
                rating: assignmentData.rating || null,
                richText: '<p>The venture is viable and innovative, with a defined market opportunity, a demonstrated competitive advantage, a sound financial plan, and a team with the relevant skills and experience to develop, market, sell, and scale their product or service to effectively address persistent or emerging issue(s) in education and achieve long term success.</p>'
            }]))
    }, [assignmentData])

    const activeAssignmentIdx = (props.assignments || [])
        .map((obj, idx) => (obj.applicant || {}).id === params.userId
            ? idx
            : null)
        .filter(int => int !== null)[0]

    const nextAssignment = activeAssignmentIdx >= 0
        && props.assignments[activeAssignmentIdx + 1]

    const nextLink = nextAssignment
        ? `/dashboard/ebpc/applications/${nextAssignment.applicant.id}`
        : '/dashboard/ebpc/assignments'

    const isNextLinkDisabled = !assignmentData.rating
        || assignmentData.invest === null
        || Object.keys(assignmentData.ebpc_ratings)
            .filter(key => assignmentData.ebpc_ratings[key] === null)
            .length

    const isSubmitted = (props.assignments || []).length
        && (props.assignments || [])
            .filter(obj => obj.submitted_at)
            .length
                === (props.assignments || []).length

    const onUpdate = async (e) => {
        const resource = e.target.getAttribute('data-resource')
            || 'ebpc_assignments'

        if (resource === 'ebpc_ratings'
                && !assignmentData.ebpc_ratings[e.target.name])
            await graphqlClient
                .request(gql`
                    mutation {
                        insert_ebpc_ratings_one (object: {
                            ebpc_judge_id: "${authProvider.getIdentity().id}",
                            ebpc_application_id: ${props.assignment.ebpc_application.id},
                            ebpc_finals: ${props.assignment.ebpc_finals || false},
                            type: "${e.target.name}",
                            rating: ${e.target.value}
                        }) {
                            id
                        }
                    }
                `)
                .catch(err => console.warn(err))

        if (resource === 'ebpc_ratings'
                && assignmentData.ebpc_ratings[e.target.name])
            await graphqlClient
                .request(gql`
                    mutation {
                        update_ebpc_ratings (
                            where: {
                                ebpc_judge_id: { _eq: "${authProvider.getIdentity().id}" },
                                ebpc_application_id: { _eq: ${props.assignment.ebpc_application.id} },
                                ebpc_finals: {
                                    ${props.assignment.ebpc_finals === null
                                        ? gql`_is_null: true`
                                        : gql`_eq: ${props.assignment.ebpc_finals}`}
                                },
                                type: { _eq: "${e.target.name}" }
                            },
                            _set: {
                                rating: ${e.target.value}
                            }
                        ) {
                            returning {
                                id
                            }
                        }
                    }
                `)
                .catch(err => console.warn(err))

        if (resource === 'ebpc_assignments') {
            let { value } = e.target

            if (!value.match(/^(true|false|[0-9])$/))
                value = `"${value}"`

            await graphqlClient
                .request(gql`
                    mutation {
                        update_ebpc_assignments (
                            where: {
                                ebpc_judge_id: { _eq: "${authProvider.getIdentity().id}" },
                                ebpc_application_id: { _eq: ${props.assignment.ebpc_application.id} },
                                ebpc_finals: {_eq: ${props.assignment.ebpc_finals || false} },
                            },
                            _set: {
                                ${e.target.name}: ${value}
                            }
                        ) {
                            returning {
                                updated_at
                            }
                        }
                    }
                `)
        }

        const updateState = JSON.parse(JSON.stringify(assignmentData))

        if (resource === 'ebpc_ratings')
            updateState.ebpc_ratings[e.target.name] = e.target.value

        if (resource !== 'ebpc_ratings')
            updateState[e.target.name] = e.target.value

        setAssignmentData(updateState)

        history.replace(
            `${history.location.pathname}${history.location.search}`,
            updateState)

        if (typeof props.onUpdate === 'function')
            props.onUpdate(updateState)
    }

    return (
        <section className="dashboard-ebpc-application-review-rate">
            <h2 className="heading-5 margin-b">Rate this venture</h2>
            <RichText
                className="body-3 margin-3-b"
                style={{
                    color: getCssVar('--oxford')
                }}
            >
                <p>
                    Each venture responded to a series of prompts across a
                    variety of topics. Below you’ll find each category, the prompt,
                    followed by the venture’s response. Click the “Rate” button
                    next to each category and then select a score from 1-10,
                    with 10 being the best rating.
                </p>
                <p>
                    Please carefully read each response before assigning a
                    rating. You can change your rating at any time before finalizing.
                </p>
            </RichText>
            <div className="row-condensed">
                <aside className="col margin-3-b">
                    <div
                        className="card card-border pad-2-t pad-2-b body-4"
                        style={{
                            borderTop: '3px solid #28FBAC',
                            borderRadius: 0,
                            color: getCssVar('--navy')
                        }}
                    >
                        <ul className="body-4">
                            {ratingsFields
                                .concat([{
                                    slug: 'invest',
                                    title: 'Investment',
                                    rating: assignmentData.invest !== null
                                        ? (assignmentData.invest === true
                                                || assignmentData.invest === 'true'
                                            ? 'Yes'
                                            : 'No')
                                        : null
                                }])
                                .map((obj, idx) => (
                                    <li
                                        className={`row-condensed flex-justify-content-space-between${
                                            idx
                                                ? ' margin-1-t'
                                                : ''}`}
                                        key={`${formId}-aside-rating-${idx}`}
                                    >
                                        <div className="col-auto">
                                            <a
                                                href={`#${obj.slug}`}
                                                data-vertical-offset={!idx
                                                    || obj.slug
                                                        .match(/(rating|invest)/)
                                                    ? '-30'
                                                    : null}
                                            >
                                                {obj.title
                                                    .replace(/\s*rating\s*/, '')
                                                    .trim()}
                                            </a>
                                        </div>
                                        <div
                                            className="col-auto text-align-center"
                                        >
                                            <span
                                                aria-label={obj.slug !== 'invest'
                                                    ? (`Rating${obj.rating
                                                        ? `: ${obj.rating}`
                                                        : ' pending'}`)
                                                    : null}
                                            >
                                                {obj.rating || '–'}
                                            </span>
                                        </div>
                                    </li>
                                ))}
                        </ul>
                        <hr
                            style={{
                                borderColor: getCssVar('--geyser')
                            }}
                        />
                        <p
                            className="row-condensed body-5 margin-t"
                            role="status"
                            style={{
                                fontWeight: 300,
                                color: getCssVar('--slate')
                            }}
                        >
                            <span className="col-auto pad-1-r">
                                <svg width="16" height="16">
                                    <use xlinkHref="#icon-info" />
                                </svg>
                            </span>
                            <span className="col pad-0-l">
                                Ratings are saved automatically
                            </span>
                        </p>
                    </div>
                </aside>
                <div className="col-12 col-8-lg-up">
                    <Form
                        id={formId}
                    >
                        {ratingsFields
                            .map((obj, idx) => (
                                <DashboardEbpcApplicationReviewRatingField
                                    key={`${formId}-rating-field-${idx}`}
                                    formId={formId}
                                    index={idx}
                                    defaultValue={assignmentData.ebpc_ratings[obj.slug]
                                        || assignmentData[obj.slug]}
                                    disabled={isSubmitted}
                                    onChange={onUpdate}
                                    {...obj}
                                >
                                    {obj.slug === 'finances'
                                        && props.assignment.ebpc_application.data.finances.financesFile
                                        && (
                                            <p className="body-5 margin-1-t">
                                                <a
                                                    className="display-inline-flex flex-align-items-center"
                                                    href={props.assignment.ebpc_application.data.finances.financesFile}
                                                    target="_blank"
                                                    title="Opens in a new tab"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: getCssVar('--navy'),
                                                        textDecoration: 'underline'
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            marginRight: 5,
                                                            lineHeight: 1
                                                        }}
                                                    >
                                                        Review material
                                                    </span>
                                                    <svg width="16" height="16">
                                                        <use xlinkHref="#icon-launch" />
                                                    </svg>
                                                </a>
                                            </p>)}
                                </DashboardEbpcApplicationReviewRatingField>
                            ))}
                        <div
                            id="invest"
                            className="form-field margin-3-b"
                            style={{
                                padding: '30px 15px',
                                backgroundColor: getCssVar('--porcelain'),
                                borderRadius: 5
                            }}
                        >
                            <fieldset>
                                <legend className="font-weight-medium display-flex">
                                    If you were an investor, would you fund this business plan?
                                </legend>
                                <div className="display-flex margin-1-t">
                                    <input
                                        id={`${formId}-invest-no`}
                                        name="invest"
                                        type="radio"
                                        value="false"
                                        defaultChecked={assignmentData.invest === false}
                                        onChange={onUpdate}
                                        disabled={isSubmitted}
                                        required
                                    />
                                    <label
                                        id={`${formId}-invest-no-label`}
                                        htmlFor={`${formId}-invest-no`}
                                        style={{
                                            fontSize: 15,
                                            fontWeight: 500
                                        }}
                                    >
                                        No
                                    </label>
                                </div>
                                <div className="display-flex margin-1-t">
                                    <input
                                        id={`${formId}-invest-yes`}
                                        name="invest"
                                        type="radio"
                                        value="true"
                                        defaultChecked={assignmentData.invest === true}
                                        onChange={onUpdate}
                                        disabled={isSubmitted}
                                        required
                                    />
                                    <label
                                        id={`${formId}-invest-yes-label`}
                                        htmlFor={`${formId}-invest-yes`}
                                        style={{
                                            fontSize: 15,
                                            fontWeight: 500
                                        }}
                                    >
                                        Yes
                                    </label>
                                </div>
                            </fieldset>
                        </div>
                        <div
                            className="form-field margin-3-b"
                            style={{
                                padding: '30px 15px',
                                backgroundColor: getCssVar('--porcelain'),
                                borderRadius: 5
                            }}
                        >
                            <label
                                className="font-weight-medium"
                                htmlFor={`${formId}-feedback`}
                            >
                                Provide feedback to the applicant (Optional)
                            </label>
                            <p
                                className="body-4 margin-1-t margin-1-b"
                                style={{
                                    color: getCssVar('--oxford')
                                }}
                            >
                                We encourage judges to leave constructive feedback that
                                may help applicants further refine their innovation.
                                Applicants get tremendous value from specific,
                                target advice. Providing additional feedback is optional.
                            </p>
                            <TextArea
                                id={`${formId}-feedback`}
                                name="feedback"
                                defaultValue={assignmentData.feedback}
                                disabled={isSubmitted}
                                onChange={onUpdate}
                                onBlur={onUpdate}
                                onFocus={onUpdate}
                            />
                        </div>
                        <div
                            className="form-field margin-3-b"
                            style={{
                                padding: '30px 15px',
                                backgroundColor: getCssVar('--porcelain'),
                                borderRadius: 5
                            }}
                        >
                            <label
                                className="font-weight-medium"
                                htmlFor={`${formId}-feedback_admin`}
                            >
                                Add notes for competition administrators (Optional)
                            </label>
                            <p
                                className="body-4 margin-1-t margin-1-b"
                                style={{
                                    color: getCssVar('--oxford')
                                }}
                            >
                                Let the team managing the competition know what you thought about this venture or application.
                                <br />
                                <em
                                    style={{
                                        display: 'inline-block',
                                        marginTop: 15,
                                        fontSize: 14
                                    }}
                                >
                                    Comments will not be shared with applicants.
                                </em>
                            </p>
                            <TextArea
                                id={`${formId}-feedback_admin`}
                                name="feedback_admin"
                                defaultValue={assignmentData.feedback_admin}
                                disabled={isSubmitted}
                                onChange={onUpdate}
                                onBlur={onUpdate}
                                onFocus={onUpdate}
                            />
                        </div>
                    </Form>
                    <nav
                        className="row-condensed flex-justify-content-space-between"
                        aria-label="Venture applications"
                    >
                        <div className="col-auto margin-auto margin-0-r">
                            {(nextAssignment
                                    || ((props.assignments || []).length
                                        && props.assignments.length
                                            !== props.assignments
                                                .filter(obj => obj.submitted_at)
                                                .length))
                                && (
                                    <Link
                                        to={nextLink || '#'}
                                        className="btn btn-lg"
                                        aria-disabled={isNextLinkDisabled}
                                    >
                                        {nextAssignment
                                            ? 'Next venture'
                                            : 'Finalize'}
                                    </Link>)}
                        </div>
                    </nav>
                </div>
            </div>
        </section>
    )
}
